import { useState } from 'react';
import { ArrowLeft, Edit2, GitMerge, MessageSquarePlus, UserCheck, Zap, Clock, Calendar, ListPlus, UserX } from 'lucide-react';
import { FavoriteStar, AddToListSheet } from '../lists';
import { DirectionsButton } from '../directions';
import { useNavigate } from 'react-router-dom';
import type { CoachMoveFlagStatus, CustomerStatus, PersonDetail, PersonMergeStatus } from '../../types';
import { effectiveFirstName, effectiveLastName } from '../../lib/personPrecedence';
import { TemperatureIcon } from '../shared/temperatureConfig';
import { CustomerStatusBadge } from '../organizations/CustomerStatusBadge';
import { MineSourceChip } from '../shared/MineSourceChip';

interface PersonHeaderProps {
  person: PersonDetail;
  onEdit: () => void;
  onLogActivity: () => void;
  onScheduleActivity: () => void;
  onConvert?: () => void;
  canEdit: boolean;
  canLogActivity: boolean;
  onFlagMove?: () => void;
  openFlagStatus?: CoachMoveFlagStatus | null;
  /** Open non-owner heads-up — renders the "Reported to X" chip when no real flag is open. */
  openNotifyInfo?: { assigneeName: string } | null;
  onMerge?: () => void;
  openMergeStatus?: PersonMergeStatus | null;
}

/**
 * Person detail page header
 * Shows name, position, organization, customer-status badge, and action buttons
 */
export const PersonHeader = ({
  person,
  onEdit,
  onLogActivity,
  onScheduleActivity,
  onConvert,
  canEdit,
  canLogActivity,
  onFlagMove,
  openFlagStatus,
  openNotifyInfo,
  onMerge,
  openMergeStatus,
}: PersonHeaderProps) => {
  const navigate = useNavigate();
  const [addOpen, setAddOpen] = useState(false);

  // Get primary organization
  const primaryOrg = person.organizations?.find((o) => o.isPrimary) || person.organizations?.[0];
  const orgStatus: CustomerStatus | undefined = primaryOrg?.organization.customerStatus ?? undefined;

  // Build subtitle parts
  const subtitleParts: string[] = [];
  if (person.position) subtitleParts.push(person.position);
  if (primaryOrg) subtitleParts.push(primaryOrg.organization.name);

  return (
    <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Back button */}
        <button
          onClick={() => navigate('/people')}
          className="flex items-center text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 mb-4 transition-colors"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          <span className="text-sm">Back to People</span>
        </button>

        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          {/* Left side - Name, position, org */}
          <div className="min-w-0">
            <div className="flex items-center flex-wrap gap-x-2 gap-y-1.5">
              {person.temperature && (
                <TemperatureIcon temperature={person.temperature} className="h-6 w-6 flex-shrink-0" />
              )}
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 leading-tight">
                {effectiveFirstName(person)} {effectiveLastName(person)}
              </h1>
              {orgStatus && <CustomerStatusBadge status={orgStatus} size="sm" />}
              {person.blastUserId ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[0.7rem] font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
                  <Zap className="h-3 w-3" />
                  In Blast
                </span>
              ) : person.convertedAt ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[0.7rem] font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                  <Clock className="h-3 w-3" />
                  Invite Sent
                </span>
              ) : null}
              {person.departedAt && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300">
                  Departed — school unknown
                </span>
              )}
              {openFlagStatus && (
                <span
                  title="A 'coach no longer here' report is in flight — no need to re-verify"
                  className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300"
                >
                  {openFlagStatus === 'ON_CALL_LIST'
                    ? 'Reported — verification call queued'
                    : openFlagStatus === 'CONFIRMED'
                      ? 'Confirmed — pending review'
                      : openFlagStatus === 'KICKED_BACK'
                        ? 'Reported — re-verification requested'
                        : 'Reported — pending review'}
                </span>
              )}
              {!openFlagStatus && openNotifyInfo && (
                <span
                  title="A heads-up was sent to the assigned rep — no need to re-report"
                  className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300"
                >
                  Reported to {openNotifyInfo.assigneeName} — awaiting review
                </span>
              )}
              {openMergeStatus === 'RAISED' && (
                <span title="A merge request is awaiting review" className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                  Merge pending review
                </span>
              )}
            </div>
            {subtitleParts.length > 0 && (
              <p className="mt-1 text-gray-500 dark:text-gray-400 truncate">
                {subtitleParts.join(' • ')}
              </p>
            )}
            <div className="mt-1 flex items-center gap-1.5 text-sm flex-wrap">
              <UserCheck className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
              {person.assignedUser ? (
                <span className="text-gray-500 dark:text-gray-400 truncate">
                  Assigned to {person.assignedUser.displayName || person.assignedUser.email}
                </span>
              ) : (
                <span className="text-amber-600 dark:text-amber-400">Unassigned</span>
              )}
              <MineSourceChip
                mineSource={person.mineSource}
                mineDerivedFrom={person.mineDerivedFrom}
              />
            </div>
          </div>

          {/* Right side - Favorite star (always visible) + action buttons (permission-gated) */}
          <div className="flex flex-wrap items-center gap-2 sm:flex-shrink-0">
            <FavoriteStar kind="PERSON" id={person.id} size={20} />
            {primaryOrg && <DirectionsButton destination={primaryOrg.organization} />}
            {/* Add to list is PERSONAL — available to all users, outside the edit/activity gate */}
            <button
              type="button"
              onClick={() => setAddOpen(true)}
              className="btn-secondary justify-center whitespace-nowrap flex-1 sm:flex-initial"
            >
              <ListPlus className="h-4 w-4 mr-1.5" />
              Add to list
            </button>
            {onFlagMove && !openFlagStatus && (
              <button
                type="button"
                onClick={onFlagMove}
                className="btn-secondary justify-center whitespace-nowrap flex-1 sm:flex-initial"
              >
                <UserX className="h-4 w-4 mr-1.5" />
                <span className="sm:hidden">No longer here</span>
                <span className="hidden sm:inline">Coach no longer here</span>
              </button>
            )}
            {onMerge && !openMergeStatus && (
              <button type="button" onClick={onMerge} className="btn-secondary justify-center whitespace-nowrap flex-1 sm:flex-initial">
                <GitMerge className="h-4 w-4 mr-1.5" />
                Merge
              </button>
            )}
            {(canLogActivity || canEdit) && (
              <>
                {canEdit && !person.blastUserId && !person.convertedAt && onConvert && (
                  <button
                    data-testid="convert-button-person-header"
                    onClick={onConvert}
                    className="btn-secondary justify-center whitespace-nowrap flex-1 sm:flex-initial"
                  >
                    <Zap className="h-4 w-4 mr-1.5" />
                    Convert
                  </button>
                )}
                {canLogActivity && (
                  <>
                    <button
                      onClick={onLogActivity}
                      className="btn-secondary justify-center whitespace-nowrap flex-1 sm:flex-initial"
                    >
                      <MessageSquarePlus className="h-4 w-4 mr-1.5" />
                      <span className="sm:hidden">Log</span>
                      <span className="hidden sm:inline">Log Activity</span>
                    </button>
                    <button
                      onClick={onScheduleActivity}
                      className="btn-secondary justify-center whitespace-nowrap flex-1 sm:flex-initial"
                    >
                      <Calendar className="h-4 w-4 mr-1.5" />
                      Schedule
                    </button>
                  </>
                )}
                {canEdit && (
                  <button
                    onClick={onEdit}
                    className="btn-primary justify-center whitespace-nowrap flex-1 sm:flex-initial"
                  >
                    <Edit2 className="h-4 w-4 mr-1.5" />
                    Edit
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <AddToListSheet
        open={addOpen}
        onClose={() => setAddOpen(false)}
        kind="PERSON"
        id={person.id}
        recordLabel={`${effectiveFirstName(person)} ${effectiveLastName(person)}`.trim()}
      />
    </div>
  );
};
